import { useCallback, useEffect, useState } from "react";
import { Button, Dropdown, message } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useRecoilState } from "recoil";
import { drdIconMappingVersionState } from "../../../../state/atoms";
import BrandKitApi, { BrandKitDrdIconDto } from "../../../../api/BrandKitApi";
import { DRD_ATTRIBUTES } from "./drdAttributes";
import "./assetDrdAttribute.scss";

const REMOVE_KEY = "__remove__";

const labelFor = (attribute?: string) =>
  DRD_ATTRIBUTES.find((a) => a.attribute === attribute)?.label;

/**
 * Loads the brand kit's DRD attribute->icon mapping and exposes helpers to map an uploaded icon
 * asset (by its S3 path) to a DRD attribute. This is the asset-centric editor for the same mapping
 * the Icons tab edits attribute-first; here an icon represents exactly one attribute (matches the
 * Asset Library mock). Stored as iconType UPLOAD so Timber renders the uploaded image on import.
 */
export function useAssetDrdAttributes(brandKitId: string, enabled: boolean) {
  const [mappings, setMappings] = useState<BrandKitDrdIconDto[]>([]);
  const [version, setVersion] = useRecoilState(drdIconMappingVersionState);

  const reload = useCallback(() => {
    if (!enabled || !brandKitId) return;
    BrandKitApi.getDrdIcons(brandKitId)
      .then((resp) => setMappings(resp.data || []))
      .catch(() => {});
  }, [brandKitId, enabled]);

  // Re-fetch on mount and whenever any surface bumps the shared version.
  useEffect(reload, [reload, version]);

  const attributeForPath = useCallback(
    (path?: string): string | undefined => {
      if (!path) return undefined;
      return mappings.find((m) => m.iconType === "UPLOAD" && m.iconKey === path)?.attribute;
    },
    [mappings]
  );

  const setAttribute = useCallback(
    (path: string, altText: string | undefined, attributeKey: string) => {
      const attr = DRD_ATTRIBUTES.find((a) => a.attribute === attributeKey);
      if (!attr || !path) return;
      // An icon represents exactly one attribute: clear any other attribute that points at this
      // same asset before assigning the chosen one.
      const prior = mappings.filter(
        (m) => m.iconType === "UPLOAD" && m.iconKey === path && m.attribute !== attributeKey
      );
      Promise.all(prior.map((p) => BrandKitApi.deleteDrdIcon(brandKitId, p.attribute).catch(() => {})))
        .then(() =>
          BrandKitApi.upsertDrdIcon(brandKitId, attributeKey, {
            attribute: attributeKey,
            type: attr.type,
            iconKey: path,
            iconType: "UPLOAD",
            altText: altText || null,
            hidden: false,
          })
        )
        .then((resp) => {
          const priorAttrs = new Set(prior.map((p) => p.attribute));
          setMappings((prev) => [
            ...prev.filter((m) => m.attribute !== attributeKey && !priorAttrs.has(m.attribute)),
            resp.data,
          ]);
          setVersion((v) => v + 1);
          message.success("Attribute mapped");
        })
        .catch(() => message.error("Failed to map attribute"));
    },
    [brandKitId, mappings, setVersion]
  );

  const clearAttribute = useCallback(
    (path: string) => {
      const targets = mappings.filter((m) => m.iconType === "UPLOAD" && m.iconKey === path);
      if (!targets.length) return;
      Promise.all(targets.map((t) => BrandKitApi.deleteDrdIcon(brandKitId, t.attribute)))
        .then(() => {
          const cleared = new Set(targets.map((t) => t.attribute));
          setMappings((prev) => prev.filter((m) => !cleared.has(m.attribute)));
          setVersion((v) => v + 1);
        })
        .catch(() => message.error("Failed to remove attribute"));
    },
    [brandKitId, mappings, setVersion]
  );

  return { mappings, attributeForPath, setAttribute, clearAttribute };
}

interface AssetAttributeCellProps {
  attribute?: string;
  onSelect: (attributeKey: string) => void;
  onClear: () => void;
}

/** The "Attribute" cell: a dropdown of canonical attributes; shows the mapped attribute + pencil, or a Select Attribute button. */
export function AssetAttributeCell({ attribute, onSelect, onClear }: AssetAttributeCellProps) {
  const menu = {
    items: [
      {
        key: "g",
        type: "group" as const,
        label: "Select Attribute",
        children: [...DRD_ATTRIBUTES]
          .sort((a, b) => a.label.localeCompare(b.label))
          .map((a) => ({ key: a.attribute, label: a.label })),
      },
      ...(attribute ? [{ type: "divider" as const }, { key: REMOVE_KEY, label: "Remove attribute", danger: true }] : []),
    ],
    onClick: ({ key }: { key: string }) => (key === REMOVE_KEY ? onClear() : onSelect(key)),
  };

  return (
    <Dropdown menu={menu} trigger={["click"]} overlayClassName="asset-attr-menu">
      {attribute ? (
        <span className="asset-attr-mapped">
          {labelFor(attribute) || attribute}
          <EditOutlined className="asset-attr-edit" />
        </span>
      ) : (
        <Button className="asset-attr-select-btn">Select Attribute</Button>
      )}
    </Dropdown>
  );
}
